import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Camera } from "lucide-react";
import { toast } from "sonner";

// Live camera QR reader (back camera). Runs while mounted; calls onScan once with the decoded text.
export function QrScanner({ onScan, onError, className = "" }) {
  const regionId = useRef(`qr-region-${Math.random().toString(36).slice(2)}`).current;
  useEffect(() => {
    let stopped = false;
    const scanner = new Html5Qrcode(regionId, { verbose: false });
    const clear = () => { try { scanner.clear(); } catch { /* already cleared */ } };
    const stop = () => {
      if (stopped) return Promise.resolve();
      stopped = true;
      // stop() throws straight away (not a rejected promise) when the camera never started,
      // e.g. no camera or permission denied; an uncaught throw here blanks the whole page.
      try {
        if (!scanner.isScanning) { clear(); return Promise.resolve(); }
        return scanner.stop().catch(() => {}).finally(clear);
      } catch {
        clear();
        return Promise.resolve();
      }
    };
    scanner.start({ facingMode: "environment" },
      { fps: 10, qrbox: (w, h) => { const s = Math.floor(Math.min(w, h) * 0.72); return { width: s, height: s }; } },
      (text) => { stop().then(() => onScan(text.trim())); },
      () => {})
      .then(() => {
        // Closed while the camera was still starting: switch it off now.
        if (stopped) { try { scanner.stop().catch(() => {}).finally(clear); } catch { clear(); } }
      })
      .catch((err) => {
        toast.error(`Camera not available: ${err?.message || err}. Allow camera permission, or type the code.`);
        onError?.(err);
      });
    return () => { stop(); };
  }, []); // eslint-disable-line
  return <div id={regionId} className={`w-full overflow-hidden bg-black ${className}`} />;
}

// Button that opens the camera in a dialog.
export default function QrScanButton({ onScan, label = "Scan QR", className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)} className={`h-12 ${className}`} data-testid="qr-scan-btn">
        <Camera className="w-5 h-5 mr-1" /> {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="qr-scan-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">Scan QR Code</DialogTitle></DialogHeader>
          {open && <QrScanner className="rounded-md min-h-[260px]" onScan={(t) => { setOpen(false); onScan(t); }} onError={() => setOpen(false)} />}
          <p className="text-xs text-slate-500 text-center">Point the camera at the ticket / token QR code. It scans automatically.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
